import Controller from "@/controllers/index";
import ConfigurationView from "../constants/ConfigurationView";
import store from "@/stores/store";
import PromisseStatus from "@/constants/errors-http/PromisseStatus";
import i18n from "@/i18n";

/**
 * Cria o state de LimitConfigurator.
 */
const EmptyLimitConfiguratorState = function()
{
	return {
		showStepper:                   false,
		configuratorView:              ConfigurationView.CREATE_OR_UPDATE,
		availableSignals:              [],
		selectedAssets:                [],
		selectedSignal:                {},
		limitAndNotificationsSettings: {},
		frequencyLimitModels:          [],
		updateResults:                 [],
		limitsDisable:                 false,
		isLoading:                     false,
		feedbackMessage:               "",
		turbinePartialError:           "",
	};
};

/**
 * Cria o objeto modelo de criação de limite e notificação do configurador de componente de frequência.
 * @param {number} alarm
 * @param {array} emailsToNotifyAlarm
 * @param {array} emailsToNotifyTrip
 * @param {number} enabledNotificationsOption
 * @param {number} frequencySettingId
 * @param {number} generatingUnitMonitoringElementId
 * @param {number} limitIntervalHours
 * @param {number} limitMaxNotifications
 * @param {number} debounceTriggerTrend
 * @param {number} trip
 * @returns {object} Objeto modelo.
 */
const FactoryCreateFrequencyLimitModel = function(
	alarm,
	emailsToNotifyAlarm,
	emailsToNotifyTrip,
	enabledNotificationsOption,
	frequencySettingId,
	generatingUnitMonitoringElementId,
	generatingUnitName,
	limitIntervalHours,
	limitMaxNotifications,
	debounceTriggerTrend,
	trip,
)
{
	return {
		alarm:                             alarm,
		emailsToNotifyAlarm:               emailsToNotifyAlarm,
		emailsToNotifyTrip:                emailsToNotifyTrip,
		enabledNotificationsOption:        enabledNotificationsOption,
		frequencySettingId:                frequencySettingId,
		generatingUnitMonitoringElementId: generatingUnitMonitoringElementId,
		generatingUnitName:                generatingUnitName,
		limitIntervalHours:                limitIntervalHours,
		limitMaxNotifications:             limitMaxNotifications,
		debounceTriggerTrend:              debounceTriggerTrend,
		trip:                              trip,
	};
};

/**
 * Cria o objeto modelo de atualização de limite e notificação do configurador de componente de frequência.
 * @param {number} alarm
 * @param {array} emailsToNotifyAlarm
 * @param {array} emailsToNotifyTrip
 * @param {number} enabledNotificationsOption
 * @param {number} frequencySettingId
 * @param {number} frequencyTrendsLimitsId
 * @param {number} generatingUnitMonitoringElementId
 * @param {string} generatingUnitName
 * @param {number} limitIntervalHours
 * @param {number} limitMaxNotifications
 * @param {number} debounceTriggerTrend
 * @param {number} trip
 * @returns {object} Objeto modelo.
 */
const FactoryUpdateFrequencyLimitModel = function(
	alarm,
	emailsToNotifyAlarm,
	emailsToNotifyTrip,
	enabledNotificationsOption,
	frequencySettingId,
	frequencyTrendsLimitsId,
	generatingUnitMonitoringElementId,
	generatingUnitName,
	limitIntervalHours,
	limitMaxNotifications,
	debounceTriggerTrend,
	trip
)
{
	return {
		alarm:                             alarm,
		emailsToNotifyAlarm:               emailsToNotifyAlarm,
		emailsToNotifyTrip:                emailsToNotifyTrip,
		enabledNotificationsOption:        enabledNotificationsOption,
		frequencySettingId:                frequencySettingId,
		frequencyTrendsLimitsId:           frequencyTrendsLimitsId,
		generatingUnitMonitoringElementId: generatingUnitMonitoringElementId,
		generatingUnitName:                generatingUnitName,
		limitIntervalHours:                limitIntervalHours,
		limitMaxNotifications:             limitMaxNotifications,
		debounceTriggerTrend:              debounceTriggerTrend,
		trip:                              trip,
	};
};

/**
 * Cria o objeto modelo de deleção de limite e notificação do configurador de componente de frequência.
 * @param {number} id
 * @param {boolean} limitsDisable
 * @returns {object} Objeto modelo.
 */
const FactoryDeleteFrequencyLimitModel = function(id, limitsDisable)
{
	return {
		id:            id,
		limitsDisable: limitsDisable,
	};
};

/**
 * Função de fábrica para criar um modelo vazio de limite de frequência.
 * @returns Um modelo vazio de limite de frequência.
 */
const EmptyCreateFrequencyLimitFactory = function()
{
	return {
		generatingUnitMonitoringElementId: 0,
		generatingUnitName:                "",
		frequencySettingId:                0,
		frequencyTrendsLimitsId:           0,
		isLimitEnabled:                    false,
		frequencyTrendName:                "",
		alarm:                             0,
		trip:                              0,
		isNotificationEnabled:             false,
		enabledNotificationsOption:        0,
		emailsToNotifyAlarm:               [],
		emailsToNotifyTrip:                [],
		limitMaxNotifications:             0,
		limitIntervalHours:                0,
		debounceTriggerTrend:              null,
	};
};

/**
 * Função de fábrica para criar um modelo de limite de frequência a partir de uma visão fornecida.
 * @param view - Objeto modelo inicializado.
 * @returns {object} Um modelo de limite de frequência.
*/
const CreateFrequencyLimitFactory = function(view = EmptyCreateFrequencyLimitFactory())
{
	return {
		generatingUnitMonitoringElementId: view.generatingUnitMonitoringElementId,
		generatingUnitName:                view.generatingUnitName,
		frequencySettingId:                view.frequencySettingId,
		frequencyTrendsLimitsId:           view.frequencyTrendsLimitsId,
		isLimitEnabled:                    view.isLimitEnabled,
		frequencyTrendName:                view.frequencyTrendName,
		alarm:                             view.alarm,
		trip:                              view.trip,
		enabledNotificationsOption:        view.enabledNotificationsOption,
		isNotificationEnabled:             view.isNotificationEnabled,
		emailsToNotifyAlarm:               view.emailsToNotifyAlarm ?? [],
		emailsToNotifyTrip:                view.emailsToNotifyTrip ?? [],
		limitMaxNotifications:             view.limitMaxNotifications ?? 0,
		limitIntervalHours:                view.limitIntervalHours ?? 0,
		debounceTriggerTrend:              view.debounceTriggerTrend ?? null,
	};
};

/**
 * Obtém os modelos de configuração de limites de tendência de frequência.
 * @param ids - IDs dos elementos de monitoramento.
 * @returns {promise} Promessa que resolve quando os modelos de configuração forem obtidos.
 */
const GetFrequencyTrendLimitsConfigurationModels = async function(ids = [ 0 ])
{
	const resp = await Controller.FrequencyTrendLimitConfigurations.GetFrequencyTrendLimitsConfigurationModels(ids);
	if (resp.status === 204) return [];

	const FrequencyLimitModels = resp.data.map((model) =>
	{
		return CreateFrequencyLimitFactory(model);
	});

	return FrequencyLimitModels;
};

/**
 * Atualiza os modelos de configuração de limites de tendência de frequência.
 * @param model - O modelo de configuração de limites de tendência de frequência a ser atualizado.
 * @param id - O id do limite do componente de frequência.
 * @returns {promise} Promessa que resolve com a resposta da atualização.
 */
const UpdateFrequencyTrendLimitsConfiguration = async function(model = FactoryUpdateFrequencyLimitModel())
{
	const resp = await Controller.FrequencyTrendLimitConfigurations.UpdateFrequencyTrendLimitsConfiguration(model);

	return resp.status === 204 ? [] : resp.data;
};

/**
 * Cria os modelos de configuração de limites de tendência de frequência.
 * @param model - O modelo de configuração de limites de tendência de frequência a ser criado.
 * @returns {promise} Promessa que resolve com a resposta da atualização.
 */
const CreateFrequencyTrendLimitsConfiguration = async function(model = FactoryCreateFrequencyLimitModel())
{
	const resp = await Controller.FrequencyTrendLimitConfigurations.CreateFrequencyTrendLimitsConfiguration(model);

	return resp.status === 204 ? [] : resp.data;
};

/**
 * Apaga os modelos de configuração de limites de tendência de frequência.
 * @param model - O modelo de configuração de limites de tendência de frequência a ser deletado.
 * @returns {promise} Promessa que resolve com a resposta da atualização.
 */
const DeleteFrequencyTrendLimitsConfiguration = async function(model = FactoryDeleteFrequencyLimitModel())
{
	const resp = await Controller.FrequencyTrendLimitConfigurations.DeleteFrequencyTrendLimitsConfiguration(model);
	return resp.status === 204 ? [] : resp.data;
};

const GetFeedbackMessage = function(results)
{
	if (results.length === 0) return "";
	const configuratorView = store.state.FrequencyTrendLimitConfiguration.LimitConfigurator.configuratorView;
	const isCreateOrUpdate =  configuratorView === ConfigurationView.CREATE_OR_UPDATE;

	const allSuccessful = results.every((result) => result.status === PromisseStatus.FULFILLED);
	const allFailed = results.every((result) => result.status === PromisseStatus.REJECTED);

	if (allSuccessful)
	{
		return isCreateOrUpdate ?
			`${i18n.t("Labels.FrequencyTrendLimitConfiguration.CreateConfigurationSuccess")}` :
			`${i18n.t("Labels.FrequencyTrendLimitConfiguration.DeleteConfigurationSuccess")}`;
	}
	else if (allFailed)
	{
		return `${i18n.t("Labels.FrequencyTrendLimitConfiguration.CreateConfigurationError")}`;
	}
	else
	{
		return `${i18n.t("Labels.FrequencyTrendLimitConfiguration.CreateConfigurationPartialSuccess")}`;
	}
};

const GetTurbinesPartialErrors = function(results)
{
	const failedResults = results.filter((result) => result.status === PromisseStatus.REJECTED);
	const successfulResults = results.filter((result) => result.status === PromisseStatus.FULFILLED);
	if (failedResults.length > 0 && successfulResults.length > 0)
	{
		return failedResults.map((result) => result.turbine).join(", ");
	}
	else
	{
		return "";
	}
};

export default {
	EmptyLimitConfiguratorState,
	GetFrequencyTrendLimitsConfigurationModels,
	UpdateFrequencyTrendLimitsConfiguration,
	CreateFrequencyTrendLimitsConfiguration,
	FactoryUpdateFrequencyLimitModel,
	FactoryCreateFrequencyLimitModel,
	FactoryDeleteFrequencyLimitModel,
	DeleteFrequencyTrendLimitsConfiguration,
	GetFeedbackMessage,
	GetTurbinesPartialErrors
};
